import React from 'react'

import { AxiosPromise } from 'axios'
import cx from 'classnames'
import Qs from 'qs'
import { either,isEmpty, isNil } from 'ramda'
import useSWR from 'swr'

import { ProgressiveLoader } from '@driftt/tide-core'
import { A, ToolTip } from '@driftt/tide-core'

import { CustomerApi } from 'api'
import { prefixUrlWithHttp } from 'utils/activity'
import { conversationUrl } from 'utils/paths'
import { getTimestampActivityFormat } from 'utils/timeStamp'

import BaseCard from '../BaseCard'
import { CardHeader, CardHeaderExpandProps } from '../CardHeader'

import sharedStyles from '../../../styles/shared.module.css'
import styles from './styles.module.css'

export const RecentActivityCardHeader: React.FC<CardHeaderExpandProps> = ({ ...rest }) => (
  <CardHeader data-ocweb-id="RecentActivityCardHeader" content="Recent activity" {...rest} />
)

enum TimelineEventType {
  NOTE = 'NOTE',
  NOTE_CALL = 'NOTE_CALL',
  NOTE_MEETING = 'NOTE_MEETING',
  NOTE_EMAIL = 'NOTE_EMAIL',
  CONVERSATION = 'CONVERSATION',
  SEGMENT_ENTER = 'SEGMENT_ENTER',
  SEGMENT_LEAVE = 'SEGMENT_LEAVE',
  ADDED = 'ADDED',
  TRIGGERED_EVENT = 'TRIGGERED_EVENT',
  TAG_ADD = 'TAG_ADD',
  TAG_REMOVE = 'TAG_REMOVE',
  NPS_SENT = 'NPS_SENT',
  NPS_SEEN = 'NPS_SEEN',
  NPS_RESPONDED = 'NPS_RESPONDED',
  MESSAGE_SENT = 'MESSAGE_SENT',
  MESSAGE_SEEN = 'MESSAGE_SEEN',
  MESSAGE_CTA_CLICKED = 'MESSAGE_CTA_CLICKED',
  PROPERTY = 'PROPERTY',
  EMAIL_CAPTURE_SUBMITTED = 'EMAIL_CAPTURE_SUBMITTED',
  BOOKED_MEETING = 'BOOKED_MEETING'
}

interface TimelinePayload {
  endUserId: number
  eventType: TimelineEventType
  createdAt: number
  body?: {
    guid: string
    id: string
    userId?: number
    anonymousId?: number
    companyId?: number
    event: string
    attributes?: { url?: string; title?: string }
    context: { ip?: string }
    createdAt: number
    orgId: number
    internal?: boolean
    driftId?: number
    isDebug?: boolean
    assignee?: {
      alias?: string
      email?: string
    }
    conversation?: {
      displayId: number
      inboxId: number
    }
  }
}

interface TimelinePageEvent {
  title?: string
  url?: string
  createdAt: number
}

interface ConversationEventProps {
  id?: number
  createdAt: number
  agent?: string
  url: string
}

const getContactTimeline = ({ endUserId }: { endUserId: number }): AxiosPromise<any> =>
  CustomerApi.get(`end_users/${endUserId}/timeline`, {
    params: { size: 30, offset: 0, filter: ['EVENT', 'CONVERSATION'] },
    paramsSerializer: (params: any) => Qs.stringify(params, { arrayFormat: 'repeat' })
  })

const pageEvent = (event: TimelinePayload) =>
  event.eventType === TimelineEventType.TRIGGERED_EVENT && event.body?.event === 'page'

const conversationEvent = (event: TimelinePayload) =>
  event.eventType === TimelineEventType.CONVERSATION

export const useContactTimeline = ({
  endUserId
}: {
  endUserId: number
}): { data?: TimelinePayload[]; error?: Error } => {
  const { data, error } = useSWR<TimelinePayload[], Error>(
    `contactTimeline-${endUserId}`,
    async () => {
      const { data } = await getContactTimeline({ endUserId })
      return data.filter((e: TimelinePayload) => pageEvent(e) || conversationEvent(e)).slice(0, 10)
    },
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  )

  return { data, error }
}

const toTimelinePageEvent = (event: TimelinePayload) => {
  return {
    title: event.body?.attributes?.title,
    url: event.body?.attributes?.url,
    createdAt: event.createdAt
  }
}

const PageEvent = ({ event }: { event: TimelinePageEvent }) => {
  const isBlank = either(isNil, isEmpty)

  if (isBlank(event.url) && isBlank(event.title)) {
    return null
  }

  const url = event.url && prefixUrlWithHttp(event.url)

  return (
    <div className={sharedStyles.ellipsis}>
      <span className={styles.type}>{`Visited `}</span>
      <ToolTip component={url} tethered flow={'auto'} className={styles.eventToolTip}>
        <A type="primary" href={url} target="_blank" rel="noreferrer noopener">
          {event.title ?? url}
        </A>
      </ToolTip>
    </div>
  )
}

const toConversationEvent = (event: TimelinePayload): ConversationEventProps => {
  return {
    id: event.body?.conversation?.displayId,
    agent: event.body?.assignee?.alias ?? event.body?.assignee?.email ?? `Bot`,
    url: conversationUrl(event.body?.conversation?.displayId),
    createdAt: event.createdAt
  }
}

const ConversationEvent = ({ event }: { event: ConversationEventProps }) => {
  return (
    <div className={sharedStyles.ellipsis}>
      <span className={styles.type}>{`Conversation with `}</span>
      <ToolTip
        component={'View conversation'}
        tethered
        flow={'auto'}
        className={styles.eventToolTip}>
        <A type="primary" href={event.url} target="_blank" rel="noreferrer noopener">
          {event.agent}
        </A>
      </ToolTip>
    </div>
  )
}

const Event: React.FC<{ event: TimelinePayload; currentlyViewing: boolean }> = ({
  event,
  currentlyViewing
}) => {
  return (
    <div className={styles.pageViewEvent}>
      <div
        className={cx(styles.pageViewEventCircle, {
          [styles.currentlyViewing]: currentlyViewing
        })}></div>
      <div className={styles.eventContent}>
        <div>{getTimestampActivityFormat(event.createdAt)}</div>
        {pageEvent(event) && <PageEvent event={toTimelinePageEvent(event)} />}
        {conversationEvent(event) && <ConversationEvent event={toConversationEvent(event)} />}
      </div>
    </div>
  )
}

const RecentActivityLoading: React.FC<{ count: number }> = ({ count }) => {
  const placeholderShape = [<rect key={1} x="0" y="0" width={100} height={12} />]
  const options = {
    primaryColor: 'var(--color-p1-4)',
    secondaryColor: 'var(--color-p1-5)',
    primaryOpacity: 1,
    secondaryOpacity: 1,
    delayKey: 1,
    height: 15,
    width: 200
  }

  return (
    <>
      {[...Array(count)].map((_, index) => (
        <div className={styles.pageViewEvent} key={`placeholder-${index}`}>
          <div
            className={cx(styles.pageViewEventCircle, {
              [styles.currentlyViewing]: false
            })}></div>
          <ProgressiveLoader
            className={styles.loaderItem}
            options={options}
            placeholderShape={placeholderShape}
            numPlaceholders={1}
          />
        </div>
      ))}
    </>
  )
}

export const RecentActivityCard: React.FC<{ endUserId: number }> = ({ endUserId }) => {
  const { data: timelineEvents } = useContactTimeline({ endUserId })

  return (
    <BaseCard data-ocweb-id="RecentActivityCard">
      {!timelineEvents ? (
        <>
          <div className={styles.timeline}>
            <RecentActivityLoading count={5} />
          </div>
        </>
      ) : timelineEvents.length > 0 ? (
        <>
          <div className={styles.timeline}>
            {timelineEvents.map(event => (
              <Event key={`${event.createdAt}`} event={event} currentlyViewing={false} />
            ))}
          </div>
        </>
      ) : (
        <div className={styles.noPagesViewedRecentlyCopy}>No recent activity</div>
      )}
      <div data-ocweb-id="ViewFullActivityTimeline" className={styles.viewFullActivityTimeline}>
        <A
          target="_blank"
          href={`/contacts/details/${endUserId}?showActivity=true`}
          rel="noreferrer noopener">
          View full activity timeline
        </A>
      </div>
    </BaseCard>
  )
}
